import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Send,
  Plus,
  ArrowLeft,
  Sparkles,
  Megaphone,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  X,
  RefreshCw,
  Users,
  Wallet,
  AlertCircle,
  HelpCircle,
  Info,
  ExternalLink,
  ChevronRight,
  Eye,
  Search,
  MessageSquare,
} from "lucide-react";
import EmptyState from "../../../atoms/Dashboard/WhatsApp/EmptyState";
import { useCampaigns } from "../../../../hooks/useCampaigns";
import { useTemplates } from "../../../../hooks/useTemplates";
import { SkeletonListRows } from "../../../atoms/Dashboard/WhatsApp/SkeletonLoader";
import type { WhatsAppCampaign, WhatsAppTemplate } from "../../../../Data/whatsapp.dto";
import CampaignDetailModal from "./CampaignDetailModal";
import { showErrorToast, showSuccessToast } from "../../../atoms/Utlis/Toast";
import ReactSelect from "react-select";
import {
  Page,
  Hero,
  HeroGlow,
  HeroContent,
  HeroLeft,
  BackBtn,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  HeroActions,
  RefreshIconButton,
  CreateBtn,
  StatsStrip,
  StatChip,
  StatIcon,
  StatValue,
  StatLabel,
  ErrorBanner,
  FormAlertBox,
  CreatePanel,
  PanelHeader,
  PanelTitleRow,
  PanelTitle,
  PanelSubtitle,
  CloseCreatorBtn,
  WizardBody,
  SectionBlock,
  SectionHeaderRow,
  StepBadge,
  BlockTitle,
  BlockSubtitle,
  Field,
  Label,
  HelperText,
  Input,
  Select,
  FieldGrid2,
  NoTemplateNotice,
  TemplateSnippetCard,
  SnippetHeader,
  SnippetTitle,
  SnippetCategory,
  SnippetPurposeNote,
  SnippetBody,
  SnippetFooter,
  AudienceRadioGroup,
  RadioCard,
  RadioDot,
  RadioTitle,
  RadioDesc,
  AudiencePreviewCard,
  AudienceCountWrap,
  AudienceCountNumber,
  AudienceCountLabel,
  RecipientsSampleRow,
  RecipientPill,
  PersonalizationSplitGrid,
  PersonalizationLeftCol,
  PersonalizationPreviewCol,
  VariablesMappingGrid,
  VarCard,
  VarCardHeader,
  VarBadge,
  VarLabel,
  TokenChipsGroup,
  TokenChipTitle,
  TokenChip,
  PreviewContainer,
  PhoneHeader,
  PhoneAvatar,
  PhoneTitle,
  PhoneStatus,
  PhoneChatArea,
  ChatBubble,
  ChatBody,
  ChatFooter,
  ChatMeta,
  DoubleCheckMark,
  PreviewButtonsList,
  PreviewButtonPill,
  PreviewFooterNote,
  WalletEstimateGrid,
  EstimateMetricCard,
  MetricLabelText,
  MetricBigValue,
  MetricSubText,
  EstimateLoadingBox,
  EstimatePromptBox,
  SufficiencyBanner,
  SufficiencyContent,
  RechargeActionBtn,
  WizardActionsBar,
  SaveDraftBtn,
  SecondaryBtn,
  PrimaryBtn,
  ListPanel,
  SearchFilterRow,
  SearchBox,
  SearchInput,
  ClearSearchBtn,
  FilterTabs,
  FilterTabBtn,
  ListBody,
  EmptyWrap,
  CampaignListGrid,
  CampaignCard,
  CampaignCardTop,
  CampaignDisplayName,
  CampaignMetaInfo,
  StatusBadge,
  CampaignMetricsRow,
  MetricTile,
  MetricTileVal,
  MetricTileLbl,
  CampaignCardActions,
  LaunchDraftBtn,
  ViewDetailsBtn,
  ModalBackdrop,
  ModalBox,
  ModalHeaderRow,
  ModalHeading,
  ModalText,
  ModalBtnRow,
  AlbumSelectActionsRow,
  AlbumActionBtn,
  campaignSelectStyles,
} from "./CampaignManagerStyles";

const STATUS_FILTERS = [
  { value: "all", label: "All Campaigns" },
  { value: "completed", label: "Completed" },
  { value: "sending", label: "Sending" },
  { value: "draft", label: "Drafts" },
  { value: "failed", label: "Failed" },
] as const;

const DYNAMIC_TOKENS = [
  { token: "{{customer_name}}", label: "Client Name", desc: "Customer's first/full name" },
  { token: "{{studio_name}}", label: "Studio Name", desc: "Your studio business name" },
  { token: "{{album_name}}", label: "Album Name", desc: "Album project title" },
  { token: "{{album_id}}", label: "Album ID", desc: "Album reference ID" },
];

const CampaignManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTemplateId = searchParams.get("template_id");

  const {
    campaigns,
    loading,
    error,
    isError,
    activeCampaignDetail,
    activeCampaignLoading,
    launchingCampaignId,
    studioAlbums,
    campaignAudience,
    campaignAudienceLoading,
    walletEstimate,
    walletEstimateLoading,
    walletBalance,
    refresh,
    createCampaign,
    launchCampaign,
    getCampaignDetail,
    fetchStudioAlbums,
    fetchCampaignAudience,
    fetchWalletEstimate,
    clearActiveCampaign,
  } = useCampaigns();

  const { templates } = useTemplates();

  // Create Campaign Form State
  const [showCreate, setShowCreate] = useState(Boolean(preselectedTemplateId));
  const [name, setName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(preselectedTemplateId || "");
  const [audienceType, setAudienceType] = useState<"all" | "album">("all");
  const [selectedAlbumIds, setSelectedAlbumIds] = useState<string[]>([]);
  const [variables, setVariables] = useState<Record<number, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter and Search State
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Campaign Detail Results Modal State
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Draft Launch Modal State
  const [draftToLaunch, setDraftToLaunch] = useState<WhatsAppCampaign | null>(null);

  // Approved templates ready to be used
  const approvedTemplates = useMemo(() => {
    return templates.filter((t) => (t.status || "").toLowerCase() === "approved");
  }, [templates]);

  // Approved template options for ReactSelect
  const templateOptions = useMemo(() => {
    const list = approvedTemplates.map((t) => {
      const notePart = t.message_purpose ? ` - "${t.message_purpose}"` : "";
      return {
        value: String(t.id),
        label: `${t.template_name || t.name} (${t.category || "General"} · ${(t.language || "en").toUpperCase()}${notePart})`,
      };
    });
    return [
      {
        value: "__CREATE_NEW_TEMPLATE__",
        label: "+ Create a New Template",
      },
      ...list,
    ];
  }, [approvedTemplates]);

  // Selected template object
  const selectedTemplate: WhatsAppTemplate | undefined = useMemo(() => {
    if (!selectedTemplateId || selectedTemplateId === "__CREATE_NEW_TEMPLATE__") return undefined;
    return templates.find((t) => String(t.id) === selectedTemplateId);
  }, [templates, selectedTemplateId]);

  const selectedTemplateOption = useMemo(() => {
    if (!selectedTemplateId || selectedTemplateId === "__CREATE_NEW_TEMPLATE__") return null;
    return templateOptions.find((opt) => opt.value === selectedTemplateId) || null;
  }, [templateOptions, selectedTemplateId]);

  // Studio album options for ReactSelect multi-select
  const albumOptions = useMemo(() => {
    return studioAlbums.map((album) => ({
      value: String(album.id),
      label: `${album.project_name || album.name || `Album #${album.id}`}${
        album.project_code ? ` (${album.project_code})` : ""
      }`,
    }));
  }, [studioAlbums]);

  const selectedAlbumOptions = useMemo(() => {
    return albumOptions.filter((opt) => selectedAlbumIds.includes(opt.value));
  }, [albumOptions, selectedAlbumIds]);

  const handleClearAllAlbums = () => {
    setSelectedAlbumIds([]);
    setFormError(null);
  };

  // Detect variables in the selected template body: {{1}}, {{2}}, ...
  const templateVariables = useMemo(() => {
    const text = selectedTemplate?.body_text || selectedTemplate?.body || "";
    if (!text) return [];
    const matches = Array.from(text.matchAll(/\{\{(\d+)\}\}/g));
    return Array.from(new Set(matches.map((m) => parseInt(m[1], 10)))).sort((a, b) => a - b);
  }, [selectedTemplate]);

  // Load studio albums on mount
  useEffect(() => {
    fetchStudioAlbums();
  }, [fetchStudioAlbums]);

  // If a template was preselected via URL, set it
  useEffect(() => {
    if (preselectedTemplateId) {
      setSelectedTemplateId(preselectedTemplateId);
      setShowCreate(true);
    }
  }, [preselectedTemplateId]);

  // Fetch Audience preview when audience selection changes
  useEffect(() => {
    if (showCreate) {
      const albumIdsParam =
        audienceType === "album" && selectedAlbumIds.length > 0 ? selectedAlbumIds : undefined;
      fetchCampaignAudience({ album_ids: albumIdsParam });
    }
  }, [showCreate, audienceType, selectedAlbumIds, fetchCampaignAudience]);

  // Fetch Wallet Estimate when template or audience selection changes
  useEffect(() => {
    if (showCreate && selectedTemplateId) {
      const albumIdsParam =
        audienceType === "album" && selectedAlbumIds.length > 0 ? selectedAlbumIds : undefined;
      fetchWalletEstimate({
        template_id: selectedTemplateId,
        album_ids: albumIdsParam,
      });
    }
  }, [showCreate, selectedTemplateId, audienceType, selectedAlbumIds, fetchWalletEstimate]);

  // Automatically suggest a campaign name if empty when template changes
  const handleTemplateSelect = (tmplId: string) => {
    if (tmplId === "__CREATE_NEW_TEMPLATE__") {
      navigate("/dashboard/whatsapp/templates?create=true");
      return;
    }
    setSelectedTemplateId(tmplId);
    setFormError(null);
    const tmpl = templates.find((t) => t.id === tmplId);
    if (tmpl && !name) {
      const readable = (tmpl.template_name || tmpl.name || "Campaign")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      setName(`${readable} Broadcast`);
    }
  };

  const handleTokenClick = (varIndex: number, token: string) => {
    setVariables((prev) => ({
      ...prev,
      [varIndex]: token,
    }));
  };

  // Submit campaign creation (launch immediately or save as draft)
  const handleCreateCampaign = async (launchNow: boolean) => {
    setFormError(null);

    if (!selectedTemplateId) {
      const msg = "Please choose an approved template for this broadcast.";
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    if (audienceType === "album" && selectedAlbumIds.length === 0) {
      const msg = "Please select at least one studio album to target, or choose 'All Studio Customers'.";
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    // Check variables: every variable must have a mapped value
    const mappedVariables: string[] = [];
    for (const vIdx of templateVariables) {
      const val = variables[vIdx]?.trim();
      if (!val) {
        const msg = `Please configure variable {{${vIdx}}} (e.g. choose {{customer_name}} or enter text).`;
        setFormError(msg);
        showErrorToast(msg);
        return;
      }
      mappedVariables.push(val);
    }

    // If launching immediately, verify wallet sufficiency if estimate exists
    if (launchNow && walletEstimate && !walletEstimate.wallet_sufficient) {
      const msg = `Insufficient wallet balance. This campaign requires ${formatCurrency(
        walletEstimate.estimated_charge_inr
      )}, but your balance is ${formatCurrency(
        walletEstimate.wallet_balance_inr
      )}. Please recharge your wallet or save as draft.`;
      setFormError(msg);
      showErrorToast(msg);
      return;
    }

    setIsSubmitting(true);
    try {
      const campaignName = name.trim() || selectedTemplate?.template_name || "New Campaign";
      const res = await createCampaign({
        name: campaignName,
        template_id: selectedTemplateId,
        album_ids: audienceType === "album" && selectedAlbumIds.length > 0 ? selectedAlbumIds : undefined,
        variables: mappedVariables,
        launch: launchNow,
      });

      if (res && !(res as any).error) {
        // Reset form
        setName("");
        setSelectedTemplateId("");
        setSelectedAlbumIds([]);
        setAudienceType("all");
        setVariables({});
        setShowCreate(false);
      } else {
        const errMsg =
          (res as any)?.payload?.message ||
          (res as any)?.error?.message ||
          (launchNow ? "Failed to launch campaign broadcast." : "Failed to save campaign draft.");
        setFormError(errMsg);
        showErrorToast(errMsg);
      }
    } catch (err: any) {
      const errMsg = err?.message || "An unexpected error occurred.";
      setFormError(errMsg);
      showErrorToast(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDetailModal = (campaignId: string) => {
    getCampaignDetail(campaignId);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    clearActiveCampaign();
  };

  const handleLaunchDraft = async (campaignId: string) => {
    try {
      const res = await launchCampaign(campaignId);
      if (res && (res as any).error) {
        const errMsg =
          (res as any)?.payload?.message ||
          (res as any)?.error?.message ||
          "Failed to launch campaign.";
        showErrorToast(errMsg);
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to launch campaign.");
    } finally {
      setDraftToLaunch(null);
    }
  };

  // Filtered campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      const st = (c.status || "").toLowerCase();
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "completed" && (st === "completed" || st === "sent")) ||
        (filterStatus === "sending" && st === "sending") ||
        (filterStatus === "draft" && st === "draft") ||
        (filterStatus === "failed" && (st === "failed" || st === "partial"));

      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.template_name || "").toLowerCase().includes(q) ||
        (c.status || "").toLowerCase().includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [campaigns, filterStatus, searchQuery]);

  // Summary stats
  const stats = useMemo(() => {
    const draft = campaigns.filter((c) => (c.status || "").toLowerCase() === "draft").length;
    const completed = campaigns.filter(
      (c) => (c.status || "").toLowerCase() === "completed" || (c.status || "").toLowerCase() === "sent"
    ).length;
    const failed = campaigns.filter(
      (c) => (c.status || "").toLowerCase() === "failed" || (c.status || "").toLowerCase() === "partial"
    ).length;
    const totalRecipients = campaigns.reduce((acc, c) => acc + (c.sent_count || 0), 0);

    return { total: campaigns.length, draft, completed, failed, totalRecipients };
  }, [campaigns]);

  const formatCurrency = (amount?: string | number) => {
    if (amount == null) return "₹0.00";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return isNaN(num) ? "₹0.00" : `₹${num.toFixed(2)}`;
  };

  const formatRecipientDisplay = (r: any): string => {
    if (!r) return "Client";
    if (typeof r === "string") return r;

    let phone =
      r.customer_number ||
      r.phone_number ||
      r.mobile_number ||
      r.phone ||
      r.mobile ||
      r.number ||
      r.integrated_number ||
      r.to ||
      r.user?.customer_number ||
      r.user?.phone_number ||
      r.user?.phone ||
      r.user?.mobile_number ||
      "";

    if (
      phone &&
      r.country_code &&
      !String(phone).startsWith("+") &&
      !String(phone).startsWith(String(r.country_code))
    ) {
      const code = String(r.country_code).startsWith("+")
        ? r.country_code
        : `+${r.country_code}`;
      phone = `${code} ${phone}`;
    }

    const name =
      r.customer_name ||
      r.name ||
      r.full_name ||
      r.client_name ||
      r.user_name ||
      r.username ||
      r.user?.customer_name ||
      r.user?.name ||
      r.user?.full_name ||
      "";

    if (name && phone) return `${name} (${phone})`;
    if (phone) return String(phone);
    if (name) return String(name);
    return "Client";
  };

  // Preview rendered body with resolved sample variables
  const previewRenderedBody = useMemo(() => {
    const text = selectedTemplate?.body_text || selectedTemplate?.body || "";
    if (!text) return "";
    let txt = text;
    templateVariables.forEach((idx) => {
      const val = variables[idx];
      let displayVal = `[Variable {{${idx}}}]`;
      if (val === "{{customer_name}}") displayVal = "Rahul Sharma";
      else if (val === "{{studio_name}}") displayVal = "KM Studio";
      else if (val === "{{album_name}}") displayVal = "Sharma Wedding 2026";
      else if (val === "{{album_id}}") displayVal = "ALB-108";
      else if (val) displayVal = val;
      txt = txt.split(`{{${idx}}}`).join(displayVal);
    });
    return txt;
  }, [selectedTemplate, templateVariables, variables]);

  return (
    <Page>
      {/* Hero Header */}
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackBtn to="/dashboard/whatsapp" aria-label="Back to WhatsApp CRM">
              <ArrowLeft size={18} />
            </BackBtn>
            <HeroText>
              <HeroBadge>
                <Sparkles size={12} />
                WhatsApp Broadcast Campaigns
              </HeroBadge>
              <PageTitle className="linear-text">Campaigns</PageTitle>
              <PageSubtitle>
                Send approved WhatsApp templates in bulk to clients who redeemed your studio's album codes.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>

          <HeroActions>
            <RefreshIconButton
              type="button"
              onClick={() => refresh(filterStatus !== "all" ? filterStatus : undefined)}
              title="Refresh campaigns and wallet"
            >
              <RefreshCw size={16} />
            </RefreshIconButton>
            <CreateBtn type="button" onClick={() => setShowCreate((v) => !v)}>
              {showCreate ? <X size={16} /> : <Plus size={16} />}
              {showCreate ? "Close Launcher" : "New Campaign"}
            </CreateBtn>
          </HeroActions>
        </HeroContent>
      </Hero>

      {/* Stats Summary Strip */}
      <StatsStrip>
        <StatChip>
          <StatIcon $tone="#c94daa">
            <Megaphone size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Campaigns</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#16a34a">
            <CheckCircle2 size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.completed}</StatValue>
            <StatLabel>Completed</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#6b7280">
            <FileText size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.draft}</StatValue>
            <StatLabel>Drafts</StatLabel>
          </div>
        </StatChip>
        <StatChip>
          <StatIcon $tone="#2563eb">
            <Users size={18} />
          </StatIcon>
          <div>
            <StatValue>{stats.totalRecipients.toLocaleString()}</StatValue>
            <StatLabel>Messages Sent</StatLabel>
          </div>
        </StatChip>
      </StatsStrip>

      {/* Global Error Notice */}
      {isError && error?.campaigns && <ErrorBanner>{error.campaigns}</ErrorBanner>}

      {/* Create & Launch Campaign Wizard */}
      {showCreate && (
        <CreatePanel>
          <PanelHeader>
            <PanelTitleRow>
              <Megaphone size={18} color="#c94daa" />
              <div>
                <PanelTitle>Create & Launch WhatsApp Broadcast</PanelTitle>
                <PanelSubtitle>
                  Target your client base, map personalized details, review wallet charges, and broadcast instantly.
                </PanelSubtitle>
              </div>
            </PanelTitleRow>
            <CloseCreatorBtn type="button" onClick={() => setShowCreate(false)}>
              <X size={18} />
            </CloseCreatorBtn>
          </PanelHeader>

          {formError && (
            <FormAlertBox>
              <AlertCircle size={16} />
              <span>{formError}</span>
            </FormAlertBox>
          )}

          <WizardBody>
            {/* Step 1: Campaign Basics & Approved Template Picker */}
            <SectionBlock>
              <SectionHeaderRow>
                <StepBadge>1</StepBadge>
                <div>
                  <BlockTitle>Campaign Name & Approved Template</BlockTitle>
                  <BlockSubtitle>
                    Choose an approved WhatsApp template to broadcast.
                  </BlockSubtitle>
                </div>
              </SectionHeaderRow>

              <FieldGrid2>
                <Field>
                  <Label>Campaign Title *</Label>
                  <Input
                    placeholder="e.g. Diwali Wedding Offer Broadcast"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  <HelperText>Internal reference for your studio team.</HelperText>
                </Field>

                <Field>
                  <Label>WhatsApp Template *</Label>
                  {approvedTemplates.length === 0 ? (
                    <NoTemplateNotice>
                      <Info size={14} color="#d97706" />
                      <span>
                        No approved templates found. Meta approval is required before broadcasting.
                      </span>
                      <Link to="/dashboard/whatsapp/templates" style={{ color: "#c94daa", fontWeight: 700 }}>
                        Create or Sync Template &rarr;
                      </Link>
                    </NoTemplateNotice>
                  ) : (
                    <ReactSelect
                      placeholder="-- Choose an approved template --"
                      options={templateOptions}
                      value={selectedTemplateOption}
                      onChange={(opt: any) => handleTemplateSelect(opt ? opt.value : "")}
                      styles={campaignSelectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      noOptionsMessage={() => "No approved templates found"}
                    />
                  )}
                  <HelperText>Only templates approved by Meta can be used in campaigns.</HelperText>
                </Field>
              </FieldGrid2>

              {/* Template Body Preview Snippet */}
              {selectedTemplate && (
                <TemplateSnippetCard style={{ marginTop: 14 }}>
                  <SnippetHeader>
                    <SnippetTitle>Template Body:</SnippetTitle>
                    <SnippetCategory>{selectedTemplate.category || "MARKETING"}</SnippetCategory>
                  </SnippetHeader>
                  {selectedTemplate.message_purpose && (
                    <SnippetPurposeNote>
                      <FileText size={12} color="#c94daa" style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        <strong>Note:</strong> {selectedTemplate.message_purpose}
                      </span>
                    </SnippetPurposeNote>
                  )}
                  <SnippetBody>{selectedTemplate.body_text || selectedTemplate.body}</SnippetBody>
                  {(selectedTemplate.footer_text || selectedTemplate.footer) && (
                    <SnippetFooter>
                      {selectedTemplate.footer_text || selectedTemplate.footer}
                    </SnippetFooter>
                  )}
                </TemplateSnippetCard>
              )}
            </SectionBlock>

            {/* Step 2: Target Audience Selection */}
            <SectionBlock>
              <SectionHeaderRow>
                <StepBadge>2</StepBadge>
                <div>
                  <BlockTitle>Select Client Audience</BlockTitle>
                  <BlockSubtitle>
                    Broadcast only to customers who redeemed album codes for your studio.
                  </BlockSubtitle>
                </div>
              </SectionHeaderRow>

              <AudienceRadioGroup>
                <RadioCard
                  $active={audienceType === "all"}
                  onClick={() => {
                    setAudienceType("all");
                    setSelectedAlbumIds([]);
                  }}
                >
                  <RadioDot $active={audienceType === "all"} />
                  <div>
                    <RadioTitle>All Studio Redeem Customers</RadioTitle>
                    <RadioDesc>
                      Send to all clients across all albums who redeemed your codes.
                    </RadioDesc>
                  </div>
                </RadioCard>

                <RadioCard
                  $active={audienceType === "album"}
                  onClick={() => setAudienceType("album")}
                >
                  <RadioDot $active={audienceType === "album"} />
                  <div>
                    <RadioTitle>Filter by Specific Albums</RadioTitle>
                    <RadioDesc>
                      Target clients from one or multiple albums (wedding, event, or portrait).
                    </RadioDesc>
                  </div>
                </RadioCard>
              </AudienceRadioGroup>

              {audienceType === "album" && (
                <Field style={{ marginTop: 12 }}>
                  <AlbumSelectActionsRow>
                    <Label style={{ margin: 0 }}>Choose Studio Albums * (Select One or More)</Label>
                    {selectedAlbumIds.length > 0 && (
                      <AlbumActionBtn type="button" onClick={handleClearAllAlbums}>
                        Clear Selection ({selectedAlbumIds.length})
                      </AlbumActionBtn>
                    )}
                  </AlbumSelectActionsRow>

                  <ReactSelect
                    isMulti
                    closeMenuOnSelect={false}
                    placeholder="Search and select one or more albums..."
                    options={albumOptions}
                    value={selectedAlbumOptions}
                    onChange={(selected: any) => {
                      const ids = selected ? selected.map((s: any) => String(s.value)) : [];
                      setSelectedAlbumIds(ids);
                      setFormError(null);
                    }}
                    styles={campaignSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    menuPosition="fixed"
                    noOptionsMessage={() => "No studio albums found"}
                  />
                  <HelperText>
                    {selectedAlbumIds.length === 0
                      ? "Search by album name or project code. You can select multiple albums."
                      : `${selectedAlbumIds.length} album${
                          selectedAlbumIds.length > 1 ? "s" : ""
                        } selected. Only customers who redeemed codes for these albums will receive this broadcast.`}
                  </HelperText>
                </Field>
              )}

              {/* Live Audience Preview Counter */}
              <AudiencePreviewCard>
                <AudienceCountWrap>
                  <Users size={20} color="#c94daa" />
                  <div>
                    <AudienceCountNumber>
                      {campaignAudienceLoading ? (
                        "Checking audience..."
                      ) : (
                        `${campaignAudience?.campaign_recipient_count ?? campaignAudience?.recipients?.length ?? campaignAudience?.count ?? 0} Clients`
                      )}
                    </AudienceCountNumber>
                    <AudienceCountLabel>
                      {campaignAudience?.count &&
                      campaignAudience?.campaign_recipient_count &&
                      campaignAudience.count > campaignAudience.campaign_recipient_count
                        ? `Unique phone numbers will receive this message (${campaignAudience.count} total album redeems deduplicated)`
                        : "Unique phone numbers will receive this message"}
                    </AudienceCountLabel>
                  </div>
                </AudienceCountWrap>

                {(() => {
                  const recipientList =
                    campaignAudience?.recipients ??
                    campaignAudience?.users ??
                    campaignAudience?.clients ??
                    campaignAudience?.audience ??
                    campaignAudience?.unique_phones ??
                    campaignAudience?.phones ??
                    [];
                  if (!Array.isArray(recipientList) || recipientList.length === 0) return null;
                  const previewList = recipientList.slice(0, 10);
                  const remaining = recipientList.length - previewList.length;
                  return (
                    <RecipientsSampleRow>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>
                        Recipients:
                      </span>
                      {previewList.map((r: any, rIdx: number) => (
                        <RecipientPill key={rIdx}>{formatRecipientDisplay(r)}</RecipientPill>
                      ))}
                      {remaining > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af" }}>
                          +{remaining} more
                        </span>
                      )}
                    </RecipientsSampleRow>
                  );
                })()}
              </AudiencePreviewCard>
            </SectionBlock>

            {/* Step 3: Dynamic Variable Mapping (If template has placeholders) */}
            {selectedTemplate && templateVariables.length > 0 && (
              <SectionBlock>
                <SectionHeaderRow>
                  <StepBadge>3</StepBadge>
                  <div>
                    <BlockTitle>Personalize Message Variables</BlockTitle>
                    <BlockSubtitle>
                      Map client tokens or type custom offer text for each variable in your template.
                    </BlockSubtitle>
                  </div>
                </SectionHeaderRow>

                <PersonalizationSplitGrid>
                  <PersonalizationLeftCol>
                    <VariablesMappingGrid>
                      {templateVariables.map((vIdx) => (
                        <VarCard key={vIdx}>
                          <VarCardHeader>
                            <VarBadge>{`{{${vIdx}}}`}</VarBadge>
                            <VarLabel>Value for variable {`{{${vIdx}}}`}</VarLabel>
                          </VarCardHeader>

                          <Input
                            placeholder="Choose token below or type custom message text"
                            value={variables[vIdx] || ""}
                            onChange={(e) =>
                              setVariables({
                                ...variables,
                                [vIdx]: e.target.value,
                              })
                            }
                          />

                          {/* 1-Click Token Inserters */}
                          <TokenChipsGroup>
                            <TokenChipTitle>1-click tokens:</TokenChipTitle>
                            {DYNAMIC_TOKENS.map((dt) => (
                              <TokenChip
                                key={dt.token}
                                type="button"
                                onClick={() => handleTokenClick(vIdx, dt.token)}
                                $isSelected={variables[vIdx] === dt.token}
                                title={dt.desc}
                              >
                                {dt.label}
                              </TokenChip>
                            ))}
                          </TokenChipsGroup>
                        </VarCard>
                      ))}
                    </VariablesMappingGrid>
                  </PersonalizationLeftCol>

                  {/* Live Message Preview (Right Side of Screen) */}
                  <PersonalizationPreviewCol>
                    <PreviewContainer>
                      <PhoneHeader>
                        <PhoneAvatar>KM</PhoneAvatar>
                        <div>
                          <PhoneTitle>Studio WhatsApp</PhoneTitle>
                          <PhoneStatus>Business Account</PhoneStatus>
                        </div>
                      </PhoneHeader>

                      <PhoneChatArea>
                        <ChatBubble>
                          <ChatBody>{previewRenderedBody}</ChatBody>

                          {(selectedTemplate.footer_text || selectedTemplate.footer) && (
                            <ChatFooter>{selectedTemplate.footer_text || selectedTemplate.footer}</ChatFooter>
                          )}

                          <ChatMeta>
                            <span>12:00 PM</span>
                            <DoubleCheckMark>✓✓</DoubleCheckMark>
                          </ChatMeta>
                        </ChatBubble>

                        {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
                          <PreviewButtonsList>
                            {selectedTemplate.buttons.map((btn, i) => (
                              <PreviewButtonPill key={i}>
                                {btn.type === "URL" ? (
                                  <ExternalLink size={12} />
                                ) : (
                                  <MessageSquare size={12} />
                                )}
                                <span>{btn.text}</span>
                              </PreviewButtonPill>
                            ))}
                          </PreviewButtonsList>
                        )}
                      </PhoneChatArea>

                      <PreviewFooterNote>
                        <Eye size={13} />
                        Live personalized client preview
                      </PreviewFooterNote>
                    </PreviewContainer>
                  </PersonalizationPreviewCol>
                </PersonalizationSplitGrid>
              </SectionBlock>
            )}

            {/* Step 4: Wallet Billing Estimation & Sufficiency */}
            <SectionBlock>
              <SectionHeaderRow>
                <StepBadge>{templateVariables.length > 0 ? "4" : "3"}</StepBadge>
                <div>
                  <BlockTitle>Cost Estimate & Studio Wallet</BlockTitle>
                  <BlockSubtitle>
                    WhatsApp conversations are debited automatically from your Studio Owner Wallet.
                  </BlockSubtitle>
                </div>
              </SectionHeaderRow>

              <WalletEstimateGrid>
                {walletEstimateLoading ? (
                  <EstimateLoadingBox>
                    <RefreshCw size={18} className="spin-animation" />
                    <span>Calculating campaign charges and checking wallet balance...</span>
                  </EstimateLoadingBox>
                ) : walletEstimate ? (
                  <>
                    <EstimateMetricCard>
                      <MetricLabelText>Rate per Recipient</MetricLabelText>
                      <MetricBigValue>
                        {formatCurrency(walletEstimate.charge_per_recipient_inr)}
                      </MetricBigValue>
                      <MetricSubText>
                        Category: {walletEstimate.pricing_category.toUpperCase()}
                      </MetricSubText>
                    </EstimateMetricCard>

                    <EstimateMetricCard>
                      <MetricLabelText>Billable Recipients</MetricLabelText>
                      <MetricBigValue>
                        {walletEstimate.billable_recipient_count}
                      </MetricBigValue>
                      <MetricSubText>Deduped unique phone numbers</MetricSubText>
                    </EstimateMetricCard>

                    <EstimateMetricCard>
                      <MetricLabelText>Estimated Total Cost</MetricLabelText>
                      <MetricBigValue style={{ color: "#c94daa" }}>
                        {formatCurrency(walletEstimate.estimated_charge_inr)}
                      </MetricBigValue>
                      <MetricSubText>Auto-debited upon broadcast</MetricSubText>
                    </EstimateMetricCard>

                    <EstimateMetricCard>
                      <MetricLabelText>Your Studio Wallet</MetricLabelText>
                      <MetricBigValue>
                        {formatCurrency(walletEstimate.wallet_balance_inr || walletBalance?.data?.balance)}
                      </MetricBigValue>
                      <MetricSubText>
                        {walletEstimate.wallet_sufficient ? "Balance Sufficient" : "Needs Recharge"}
                      </MetricSubText>
                    </EstimateMetricCard>
                  </>
                ) : (
                  <EstimatePromptBox>
                    Select a template to view the wallet cost estimate.
                  </EstimatePromptBox>
                )}
              </WalletEstimateGrid>

              {/* Wallet Sufficiency Alert */}
              {walletEstimate && (
                <SufficiencyBanner $sufficient={walletEstimate.wallet_sufficient}>
                  {walletEstimate.wallet_sufficient ? (
                    <SufficiencyContent>
                      <CheckCircle2 size={18} color="#16a34a" />
                      <div>
                        <strong>Wallet balance is sufficient!</strong>
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: "#15803d" }}>
                          You have {formatCurrency(walletEstimate.wallet_balance_inr)} available to cover the {formatCurrency(walletEstimate.estimated_charge_inr)} charge.
                        </p>
                      </div>
                    </SufficiencyContent>
                  ) : (
                    <SufficiencyContent>
                      <AlertCircle size={20} color="#dc2626" />
                      <div>
                        <strong style={{ color: "#b91c1c" }}>Insufficient Wallet Balance</strong>
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: "#b91c1c" }}>
                          This campaign requires {formatCurrency(walletEstimate.estimated_charge_inr)}, but your wallet only has {formatCurrency(walletEstimate.wallet_balance_inr)}. Please recharge your wallet before broadcasting.
                        </p>
                      </div>
                      <RechargeActionBtn
                        as={Link}
                        to="/dashboard/whatsapp/billing"
                        target="_blank"
                      >
                        <Wallet size={14} />
                        Recharge Wallet
                        <ExternalLink size={12} />
                      </RechargeActionBtn>
                    </SufficiencyContent>
                  )}
                </SufficiencyBanner>
              )}
            </SectionBlock>

            {/* Launch Actions */}
            <WizardActionsBar>
              <SecondaryBtn type="button" onClick={() => setShowCreate(false)}>
                Cancel
              </SecondaryBtn>

              <SaveDraftBtn
                type="button"
                onClick={() => handleCreateCampaign(false)}
                disabled={isSubmitting}
              >
                Save as Draft
              </SaveDraftBtn>

              <PrimaryBtn
                type="button"
                onClick={() => handleCreateCampaign(true)}
                disabled={Boolean(
                  isSubmitting ||
                  !selectedTemplateId ||
                  (walletEstimate && !walletEstimate.wallet_sufficient)
                )}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw size={16} className="spin-animation" />
                    Launching Broadcast...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Launch Broadcast Now
                  </>
                )}
              </PrimaryBtn>
            </WizardActionsBar>
          </WizardBody>
        </CreatePanel>
      )}

      {/* Campaigns List & Monitoring */}
      <ListPanel>
        <PanelHeader>
          <PanelTitleRow>
            <Megaphone size={18} color="#c94daa" />
            <div>
              <PanelTitle>Broadcast Campaigns</PanelTitle>
              <PanelSubtitle>
                {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? "s" : ""}
              </PanelSubtitle>
            </div>
          </PanelTitleRow>

          <SearchFilterRow>
            <SearchBox>
              <Search size={14} color="#9ca3af" />
              <SearchInput
                placeholder="Search campaigns by name or status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <ClearSearchBtn onClick={() => setSearchQuery("")}>
                  <X size={12} />
                </ClearSearchBtn>
              )}
            </SearchBox>

            <FilterTabs>
              {STATUS_FILTERS.map((tab) => (
                <FilterTabBtn
                  key={tab.value}
                  type="button"
                  $active={filterStatus === tab.value}
                  onClick={() => setFilterStatus(tab.value)}
                >
                  {tab.label}
                </FilterTabBtn>
              ))}
            </FilterTabs>
          </SearchFilterRow>
        </PanelHeader>

        <ListBody>
          {loading ? (
            <SkeletonListRows count={4} />
          ) : filteredCampaigns.length === 0 ? (
            <EmptyWrap>
              <EmptyState
                icon={<Megaphone size={48} />}
                title={searchQuery ? "No matching campaigns" : "No campaigns launched yet"}
                description={
                  searchQuery
                    ? "Try adjusting your search query or status filter."
                    : "Create a broadcast campaign using an approved WhatsApp template to reach your customers who redeemed album codes."
                }
              />
            </EmptyWrap>
          ) : (
            <CampaignListGrid>
              {filteredCampaigns.map((c) => {
                const st = (c.status || "draft").toLowerCase();
                const isCompleted = st === "completed" || st === "sent";
                const isDraft = st === "draft";
                const isSending = st === "sending";
                const isFailed = st === "failed" || st === "partial";
                const isLaunching = launchingCampaignId === c.id;

                return (
                  <CampaignCard key={c.id}>
                    <CampaignCardTop>
                      <div>
                        <CampaignDisplayName>{c.name}</CampaignDisplayName>
                        <CampaignMetaInfo>
                          {c.template_name || "Template"} ·{" "}
                          {c.created_at ? new Date(c.created_at).toLocaleDateString() : "Recent"}
                        </CampaignMetaInfo>
                      </div>

                      <StatusBadge $status={st}>
                        {isCompleted && <CheckCircle2 size={13} />}
                        {isSending && <RefreshCw size={13} className="spin-animation" />}
                        {isDraft && <Clock size={13} />}
                        {isFailed && <XCircle size={13} />}
                        {st.toUpperCase()}
                      </StatusBadge>
                    </CampaignCardTop>

                    <CampaignMetricsRow>
                      <MetricTile>
                        <MetricTileVal>{c.sent_count ?? 0}</MetricTileVal>
                        <MetricTileLbl>Sent</MetricTileLbl>
                      </MetricTile>
                      <MetricTile>
                        <MetricTileVal>{c.delivered_count ?? c.sent_count ?? 0}</MetricTileVal>
                        <MetricTileLbl>Delivered</MetricTileLbl>
                      </MetricTile>
                      <MetricTile>
                        <MetricTileVal style={{ color: (c.failed_count ?? 0) > 0 ? "#dc2626" : undefined }}>
                          {c.failed_count ?? 0}
                        </MetricTileVal>
                        <MetricTileLbl>Failed</MetricTileLbl>
                      </MetricTile>
                      <MetricTile>
                        <MetricTileVal style={{ color: "#c94daa" }}>
                          {formatCurrency(c.wallet_charged_inr ?? c.estimated_charge_inr)}
                        </MetricTileVal>
                        <MetricTileLbl>Wallet Debit</MetricTileLbl>
                      </MetricTile>
                    </CampaignMetricsRow>

                    <CampaignCardActions>
                      {/* Draft Launch Button */}
                      {isDraft && (
                        <LaunchDraftBtn
                          type="button"
                          onClick={() => setDraftToLaunch(c)}
                          disabled={isLaunching}
                        >
                          <Send size={13} />
                          {isLaunching ? "Launching..." : "Launch Broadcast"}
                        </LaunchDraftBtn>
                      )}

                      {/* View Report / Results */}
                      <ViewDetailsBtn
                        type="button"
                        onClick={() => handleOpenDetailModal(c.id)}
                      >
                        <Eye size={13} />
                        View Results & Recipients
                      </ViewDetailsBtn>
                    </CampaignCardActions>
                  </CampaignCard>
                );
              })}
            </CampaignListGrid>
          )}
        </ListBody>
      </ListPanel>

      {/* Campaign Detail / Results Modal */}
      <CampaignDetailModal
        isOpen={detailModalOpen}
        onClose={handleCloseDetailModal}
        campaign={activeCampaignDetail}
        loading={activeCampaignLoading}
        formatCurrency={formatCurrency}
      />

      {/* Launch Draft Confirmation Modal */}
      {draftToLaunch && (
        <ModalBackdrop onClick={() => setDraftToLaunch(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeaderRow>
              <Megaphone size={22} color="#c94daa" />
              <ModalHeading>Launch Broadcast Campaign?</ModalHeading>
            </ModalHeaderRow>
            <ModalText>
              Are you sure you want to send <strong>"{draftToLaunch.name}"</strong>? This will send approved WhatsApp templates to your clients and debit your studio owner wallet.
            </ModalText>
            <ModalBtnRow>
              <SecondaryBtn type="button" onClick={() => setDraftToLaunch(null)}>
                Cancel
              </SecondaryBtn>
              <PrimaryBtn
                type="button"
                onClick={() => handleLaunchDraft(draftToLaunch.id)}
              >
                <Send size={15} />
                Confirm & Launch Now
              </PrimaryBtn>
            </ModalBtnRow>
          </ModalBox>
        </ModalBackdrop>
      )}
    </Page>
  );
};

export default CampaignManagerPage;
